// @flow
import Locale from "./Locale";

import englishErrors from "./translations/beinformed_error_messages_en.nl";
import dutchErrors from "./translations/beinformed_error_messages_nl.nl";

import BaseCollection from "../models/base/BaseCollection";

import { IllegalArgumentException } from "../exceptions";

import type { LocaleConfiguration } from "./types";

type ErrorMessageObject = {
  [languageCode: string]: {
    [messageID: string]: string,
  },
};

type HydratedLocales = {
  _collection: Array<{
    _code: string,
    _messages: {
      [string]: string,
    },
    _errors: {
      [string]: string,
    },
  }>,
};

/**
 * Array of available locales
 * Order of locales is the preferred order of locales.
 * When user has an accept language header that does not correspond to the locales array,
 * the first one is used as the preferred language
 */
export const availableLocales: Array<LocaleConfiguration> = [
  {
    code: "en",
    messages: {},
    errors: englishErrors,
  },
  {
    code: "nl",
    messages: {},
    errors: dutchErrors,
  },
];

/**
 * Merge an object with custom error messages (exported and translated from be informed for example) and merge them with the existing translations.
 */
export const createLocalesWithConfiguredErrors = (
  configuredErrors: ErrorMessageObject,
): Array<Object> =>
  availableLocales.map((availableLocale) => {
    if (configuredErrors[availableLocale.code]) {
      return {
        ...availableLocale,
        errors: {
          ...availableLocale.errors,
          ...configuredErrors[availableLocale.code],
        },
      };
    }

    return availableLocale;
  });

/**
 * Locales, collection of Locale
 */
class Locales extends BaseCollection<Locale> {
  constructor(locales: Array<LocaleConfiguration> = availableLocales) {
    super(locales.map((locale) => new Locale(locale)));
  }

  /**
   * rehydrate
   */
  static rehydrate(hydatedLocales: HydratedLocales): Locales {
    if (hydatedLocales && hydatedLocales._collection) {
      return new Locales(
        hydatedLocales._collection.map((locale) => ({
          code: locale._code,
          messages: locale._messages,
          errors: locale._errors,
        })),
      );
    }

    return new Locales([]);
  }

  /**
   * Check if locale with localeCode exists
   */
  hasLocale(localeCode: string): boolean {
    return this.some((item) => localeCode.startsWith(item.code));
  }

  /**
   * Retrieve locales by localeCode from Locales collection
   */
  getLocale(localeCode: string): Locale {
    if (this.hasLocale(localeCode)) {
      const locale = this.find((item) => localeCode.startsWith(item.code));
      if (locale) {
        return locale;
      }
    }
    throw new IllegalArgumentException(
      `Locale configuration for locale ${localeCode} not found`,
    );
  }

  /**
   * Retrieve all available locale codes
   */
  get availableLocaleCodes(): Array<string> {
    return this.map((locale) => locale.code);
  }

  /**
   * Retrieve preferred locale based on the accept header of a browser
   */
  getPreferredLocale(acceptLanguageHeader: string | null): void | string {
    if (acceptLanguageHeader != null) {
      const acceptLanguages =
        acceptLanguageHeader.match(/[-A-Za-z]{2,10}/gu) || [];

      const preferredLocales = acceptLanguages
        .filter((locale) =>
          this.availableLocaleCodes.some((availableLocale) =>
            locale.startsWith(availableLocale),
          ),
        )
        .map((locale) =>
          this.availableLocaleCodes.find((availableLocale) =>
            locale.startsWith(availableLocale),
          ),
        );

      if (preferredLocales.length > 0) {
        return preferredLocales[0];
      }
    }

    return this.availableLocaleCodes[0];
  }

  /**
   * Add or change messages and errors for the given locale.<br>
   * It is not possible to remove messages.
   */
  update(locale: string, messages: Object = {}, errors: Object = {}) {
    if (this.hasLocale(locale)) {
      this.getLocale(locale).update(messages, errors);
    } else {
      this.add(
        new Locale({
          code: locale,
          messages,
          errors,
        }),
      );
    }
  }
}

export default Locales;
